// frontend\src\components\Forms\AddBillForm.js

import React, { useState, useEffect } from 'react';
import { Form, Button, Row, Col, Alert } from 'react-bootstrap';
import InputGroup from 'react-bootstrap/InputGroup'
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { formatDate } from '../../utils/dateUtils';
import { handleError } from '../../utils/errorHandling';
import { sanitizeInput, sanitizeNumeric } from '../../utils/sanitize';
import { addBill } from '../../services/BillService';
import { getBankAccounts } from '../../services/BankAccountService';
import { v4 as uuidv4 } from 'uuid';


const validateAmount = (amount) => {
  const numAmount = parseFloat(amount);
  return !isNaN(numAmount) &&
         numAmount > 0 &&
         numAmount < 1000000 &&
         amount.toString().length < 10;
};

const validateDueDate = (date) => {
  const now = new Date();
  const minDate = new Date();
  minDate.setFullYear(minDate.getFullYear() - 1);
  const maxDate = new Date();
  maxDate.setFullYear(maxDate.getFullYear() + 1);
  return date >= minDate && date <= maxDate;
};

const validateBillFrequency = (frequency) => {
  const validFrequencies = [
    'only_one',
    'every_month',
    'weekly',
    'fortnightly',
    'monthly',
    'quarterly',
    'yearly',
    'by_date'
  ];
  return validFrequencies.includes(frequency);
};

const validateBillName = (name) => {
  return name.trim().length > 0 && name.trim().length < 100;
};

const validateRequiredFields = (data) => {
  const errors = {};
  if (!data.billName?.trim()) errors.billName = 'Bill name is required';
  if (!data.billAmount || data.billAmount <= 0) errors.amount = 'Valid amount required';
  if (!data.billDueDate) errors.dueDate = 'Due date is required';
  if (!data.bankAccount) errors.bankAccount = 'Bank account is required';
  return errors;
};

const validateFrequencyRules = (data) => {
  const errors = {};
  if (data.billFrequency !== 'only_one') {
    if (!data.numBills || data.numBills < 1) {
      errors.numBills = 'Number of bills must be at least 1';
    }
    if (['weekly', 'fortnightly', 'monthly'].includes(data.billFrequency) && !data.dueDay) {
      errors.dueDay = 'Please select a due day';
    }
    // For other frequencies, validate the due date
    if (['by_date', 'quarterly', 'yearly'].includes(data.billFrequency)) {
      if (!data.billDueDate) {
        errors.dueDate = 'Please select a due date';
      }
    }
  }
  return errors;
};

const validateLengths = (data) => {
  const errors = {};
  if (data.billName.length > 100) errors.billName = 'Bill name too long';
  if (data.description?.length > 500) errors.description = 'Description too long';
  if (data.notes?.length > 1000) errors.notes = 'Notes too long';
  return errors;
};

// Add a new validation function for numBills
const validateNumBills = (data) => {
  // Only validate if frequency is not 'only_one'
  if (data.billFrequency !== 'only_one') {
    const numBills = parseInt(data.numBills, 10);
    
    if (isNaN(numBills) || numBills < 1) {
      return 'Number of bills must be a positive integer';
    }
    
    if (numBills > 100) {
      return 'Maximum number of bills allowed is 100';
    }
  }
  
  return true;
};

// Modify the validateForm function to include numBills validation
const validateForm = (data) => {
  const errors = {
    ...validateRequiredFields(data),
    ...validateFrequencyRules(data),
    ...validateLengths(data)
  };

  // Validate amount
  const amountError = validateAmount(data.billAmount);
  if (amountError !== true) {
    errors.amount = amountError;
  }

  // Validate due date
  const dueDateError = validateDueDate(data.billDueDate);
  if (dueDateError !== true) {
    errors.dueDate = dueDateError;
  }

  // Validate frequency
  const frequencyError = validateBillFrequency(data.billFrequency);
  if (frequencyError !== true) {
    errors.frequency = frequencyError;
  }

  // Validate bill name
  const nameError = validateBillName(data.billName);
  if (nameError !== true) {
    errors.name = nameError;
  }
  
  // Validate numBills
  const numBillsError = validateNumBills(data);
  if (numBillsError !== true) {
    errors.numBills = numBillsError;
  }

  return errors;
};

const AddBillForm = ({ onSuccess, defaultBankAccountId = null }) => {
  const [billName, setBillName] = useState('');
  const [description, setDescription] = useState('');
  const [billAmount, setBillAmount] = useState('');
  const [billFrequency, setBillFrequency] = useState('only_one');
  const [dueDay, setDueDay] = useState('');
  const [billDueDate, setBillDueDate] = useState(null);
  const [numBills, setNumBills] = useState(1);
  const [billSeriesId, setBillSeriesId] = useState('');
  const [notes, setNotes] = useState('');
  const [showNotes, setShowNotes] = useState('');
  const [lastSubmitTime, setLastSubmitTime] = useState(Date.now());
  const [errorMessage, setErrorMessage] = useState('');
  const [bankAccounts, setBankAccounts] = useState([]);
  const [bankAccount, setBankAccount] = useState('');

  useEffect(() => {
    // Fetch bank accounts when component mounts
    const fetchBankAccounts = async () => {
      try {
        const accounts = await getBankAccounts();
        setBankAccounts(accounts);
        
        // Set initial bank account based on context
        if (defaultBankAccountId && accounts.some(acc => acc.id === parseInt(defaultBankAccountId))) {
          // If we have a specific bank account context, pre-select it
          setBankAccount(defaultBankAccountId);
        } else {
          // On "all bills" page or when defaultBankAccountId doesn't exist, start with no selection
          setBankAccount('');
        }
      } catch (err) {
        setErrorMessage('Failed to load bank accounts');
      }
    };

    fetchBankAccounts();
  }, [defaultBankAccountId]);

  // Sanitized input handling
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    switch(name) {
      case 'billName':
        setBillName(sanitizeInput(value));
        break;
      case 'description':
        setDescription(sanitizeInput(value));
        break;
      case 'billAmount':
        // Only allow numbers and decimal point
        setBillAmount(sanitizeNumeric(value));
        break;
      case 'billFrequency':
        setBillFrequency(value);
        // Reset numBills if switching to 'only_one'
        if (value === 'only_one') {
          setNumBills(1);
        }
        break;
      case 'dueDay':
        setDueDay(value);
        break;
      case 'numBills':
        // Only allow positive integers - first sanitize, then ensure it's at least 1
        const numericValue = sanitizeNumeric(value);
        const parsedValue = parseInt(numericValue) || 0;
        setNumBills(Math.max(1, parsedValue));
        break;
      case 'notes':
        setNotes(sanitizeInput(value));
        break;
      case 'bankAccount':
        setBankAccount(value);
        break;
      default:
        // Handle date picker separately as it's already a Date object
        if (name === 'billDueDate') {
          setBillDueDate(value);
        }
    }
  };


  const handleToggleNotes = () => {
    setShowNotes(!showNotes);

    // Clear notes when closing
    if (showNotes) {
      setNotes('');
    }
  };

  const filterDates = (date) => {
    if (!dueDay) {
      return true; // If no pay day is selected, allow all dates
    }

    const dayOfWeek = date.getDay(); // Get the day of the week (0 = Sunday, 1 = Monday, ..., 6 = Saturday)
    const selectedDayIndex = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(dueDay);

    return dayOfWeek === selectedDayIndex;
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    // Rate limiting check
    if (Date.now() - lastSubmitTime < 1000) {
      setErrorMessage('Please wait before submitting again');
      return;
    }
    setLastSubmitTime(Date.now());

    // Form validation
    const formData = {
      billName,
      billAmount,
      billDueDate,
      billFrequency,
      dueDay,
      numBills,
      description,
      notes,
      bankAccount
    };

  const validationErrors = validateForm(formData);
  if (Object.values(validationErrors).some(error => error !== true)) {
    // Only display actual error messages, filtering out boolean values
    const errorMessages = Object.entries(validationErrors)
      .filter(([_, value]) => typeof value === 'string')
      .map(([_, message], index) => (
        <React.Fragment key={index}>
          {message}
          <br />
        </React.Fragment>
      ));

    setErrorMessage(errorMessages);
    return;
  }

    // Generate a new pay_series_id when creating a Pay
    const newBillSeriesId = uuidv4();


    // Create bill object with sanitized data
    const newBill = {
      name: sanitizeInput(billName),
      description: sanitizeInput(description),
      amount: sanitizeNumeric(billAmount),
      frequency: billFrequency,
      due_day: dueDay,
      due_date: formatDate(billDueDate),
      num_bills: billFrequency === 'only_one' ? 1 : parseInt(numBills, 10) || 1,
      bill_series_id: uuidv4(),
      notes: sanitizeInput(notes),
      bank_account: parseInt(bankAccount, 10)
    };

    try {
      const addedBill = await addBill(newBill);
      resetForm();
      
      // Check if onSuccess is a function before calling it
      if (typeof onSuccess === 'function') {
        onSuccess(addedBill);
      }
    } catch (error) {
      const message = handleError(error, 'AddBillForm');
      setErrorMessage(message);
    }
  };

 const resetForm = () => {
    setBillName('');
    setDescription('');
    setBillAmount('');
    setBillFrequency('only_one');
    setDueDay('');
    setNumBills(1);
    setNotes('');
    setBillDueDate(new Date());
    // Don't reset bank account to keep the selected option
  };

  return (
    <Form onSubmit={handleFormSubmit}>
      {errorMessage && (
        <Alert variant="danger">{errorMessage}</Alert>
      )}
      <Form.Group className="mb-3" controlId="billName">
        <Form.Label>Bill Name:</Form.Label>
        <Form.Control
          type="text"
          value={billName}
          onChange={(e) => setBillName(e.target.value)}
        />
      </Form.Group>

      <Form.Group className="mb-3" controlId="description">
        <Form.Label>Description:</Form.Label>
        <Form.Control
          type="text"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Form.Group>

      <Form.Group className="mb-3" controlId="bankAccount">
        <Form.Label>Bank Account:</Form.Label>
        <Form.Select
          name="bankAccount"
          value={bankAccount}
          onChange={(e) => setBankAccount(e.target.value)}
          required
        >
          <option value="">Select bank account</option>
          {bankAccounts.map(account => (
            <option key={account.id} value={account.id}>
              {account.name || account.nickname}
            </option>
          ))}
        </Form.Select>
      </Form.Group>

      <Form.Group className="mb-3" controlId="billAmount">
        <Form.Label>Amount:</Form.Label>
        <InputGroup>
          <InputGroup.Text>$</InputGroup.Text>
          <Form.Control
            type="number"
            value={billAmount}
            onChange={(e) => setBillAmount(e.target.value)}
            placeholder="Enter amount"
          />
        </InputGroup>
      </Form.Group>

      <Form.Group className="mb-3" controlId="billFrequency">
        <Form.Label>Frequency of Bill:</Form.Label>
        <Form.Select value={billFrequency} onChange={(e) => setBillFrequency(e.target.value)}>
          <option value="only_one">Only create this bill</option>
          <option value="by_date">Specific date each month</option>
          <option value="weekly">Weekly</option>
          <option value="fortnightly">Fortnightly</option>
          <option value="monthly">Monthly</option>
          <option value="quarterly">Quarterly</option>
          <option value="yearly">Yearly</option>
        </Form.Select>
      </Form.Group>

      {billFrequency != "only_one" && ( // Only render the following if there is not only one occurance
        <Form.Group className="mb-3" controlId="numBills">
          <Form.Label>Number of bills:</Form.Label>
          <InputGroup>
            <Form.Control
              type="number"
              min="1"
              value={numBills}
              onChange={(e) => {
                const value = e.target.value.trim();
                const parsedValue = parseInt(value, 10);
                setNumBills(value === '' ? 1 : (isNaN(parsedValue) ? 1 : Math.max(1, parsedValue)));
              }}
              placeholder="Enter number"
            />
          </InputGroup>
          <Form.Text className="text-muted">
            Specify how many occurrences of this bill should be created.
          </Form.Text>
        </Form.Group>
      )}

      { (billFrequency === 'weekly' || billFrequency === 'fortnightly' || billFrequency === 'monthly') && ( // Only render the following if the bill is not by date
        <Form.Group className="mb-3" controlId="dueDay">
          <Form.Label>Day of the week bill is due:</Form.Label>
          <Form.Select
            value={dueDay}
            name="dueDay"
            onChange={(e) => {
              setDueDay(e.target.value)}
            }
          >
            <option value="">Select pay day</option>
            <option value="Mon">Monday</option>
            <option value="Tue">Tuesday</option>
            <option value="Wed">Wednesday</option>
            <option value="Thu">Thursday</option>
            <option value="Fri">Friday</option>
            <option value="Sat">Saturday</option>
            <option value="Sun">Sunday</option>
          </Form.Select>
        </Form.Group>
      )}

      <Form.Group className="mb-3" controlId="billDueDate">
        <Form.Label>Due Date:</Form.Label>
        <br />
        <DatePicker
          selected={billDueDate}
          onChange={(date) => setBillDueDate(date)}
          dateFormat="yyyy-MM-dd"
          className="form-control"
          filterDate={filterDates}
        />
      </Form.Group>

      {showNotes && (
        <Form.Group className="mb-3" controlId="notes">
          <Form.Label>Notes:</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </Form.Group>
      )}

      <Row>
        <Col>
          <Button variant="secondary" onClick={handleToggleNotes}>
            {showNotes ? 'Close Notes' : 'Add Notes'}
          </Button>
        </Col>
        <br />
      </Row>

      <Button variant="primary" type="submit">
        Add Bill
      </Button>
    </Form>
  );
};

export default AddBillForm;
